/**
 * Custom Form Club Data Component
 * 
 * Displays the validated club ID and club name (read-only)
 * Integrates with the Globo form on the member checkout page
 */

import { Component } from '@theme/component';
import { EVENTS, GLOBO_EVENTS } from '@form/data-constants';

/**
 * Club data display web component
 * @extends {Component}
 */
class FormClubInfo extends Component {
  /**
   * Component state
   * @type {Object}
   */
  #state = {
    clubData: null,
  };

  /**
   * Event handler references for cleanup
   * @type {Object}
   */
  #handlers = {
    formDataReady: null,
    formLoaded: null,
  };

  /**
   * Initialization flag to prevent duplicate listeners
   * @type {boolean}
   */
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Initialize component
   * @private
   */
  #initialize() {
    // Prevent duplicate initialization
    if (this.#initialized) {
      return;
    }

    // Create bound handlers for cleanup
    this.#handlers.formDataReady = (event) => {
      this.#handleDataReady(event);
    };

    this.#handlers.formLoaded = () => {
      if (this.#state.clubData) {
        this.#renderClubData(this.#state.clubData);
      }
    };

    // Listen for form data ready event
    document.addEventListener(EVENTS.FORM_DATA_READY, this.#handlers.formDataReady);

    // Listen for Globo form loaded event to re-render when form is ready
    document.addEventListener(GLOBO_EVENTS.FORM_LOADED, this.#handlers.formLoaded);

    this.#initialized = true;
  }

  /**
   * Clean up event listeners
   * @private
   */
  #cleanup() {
    if (!this.#initialized) {
      return;
    }

    if (this.#handlers.formDataReady) {
      document.removeEventListener(EVENTS.FORM_DATA_READY, this.#handlers.formDataReady);
      this.#handlers.formDataReady = null;
    }

    if (this.#handlers.formLoaded) {
      document.removeEventListener(GLOBO_EVENTS.FORM_LOADED, this.#handlers.formLoaded);
      this.#handlers.formLoaded = null;
    }

    this.#initialized = false;
  }

  /**
   * Handle form data ready event
   * @param {CustomEvent} event - Data ready event
   * @private
   */
  #handleDataReady(event) {
    const { clubData } = event.detail;

    this.#state.clubData = clubData;

    // Only render if we have club data
    if (!clubData) {
      this.#renderEmpty();
      return;
    }

    this.#renderClubData(clubData);
  }

  /**
   * Render empty state (no club data)
   * @private
   */
  #renderEmpty() {
    this.innerHTML = '';
  }

  /**
   * Render club data display
   * @param {Object} clubData - Club data
   * @private
   */
  #renderClubData(clubData) {
    this.innerHTML = `
      <div class="club-data-display">
        <label class="club-data-label">
          ${this.#translate('club_id_label')}
        </label>
        <div class="club-data-info">
          <div class="club-data-info__id">
            <strong>${this.#escapeHtml(clubData.club_id)}</strong>
          </div>
          ${clubData.name ? `
            <div class="club-data-info__name">
              ${this.#escapeHtml(clubData.name)}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  /**
   * Get translation from global Etixx.translations object
   * @param {string} key - Translation key
   * @returns {string} Translated text
   * @private
   */
  #translate(key) {
    // Use global Etixx.translations populated by Liquid
    const pharmacistTranslations = window.Etixx?.translations?.pharmacist_flow || {};

    return pharmacistTranslations[key] || key;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   * @private
   */
  #escapeHtml(str) {
    if (!str) return '';

    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// Register custom element with the name used in HTML
if (!customElements.get('custom-form-club-data')) {
  customElements.define('custom-form-club-data', FormClubInfo);
}

